import React, {useState, useEffect} from 'react';
import {ArrowLeft, LoadingIcon, PrintIcon} from "../../../assets/icons/Icons";
import ProgressBar from "../../ProgressBar";
import "../../../assets/scss/components/multi-step-form.scss"
import "../../../assets/scss/components/cardholder/bulk-create-cards.scss"
import AddPayment from "./AddPayment";
import Finalize from "./Finalize";
import OrderCreationMessage from "./OrderCreationMessage";
import {useTranslation} from "react-i18next";
import Popup from "../../Popup";
import AddCard from "./AddCard";
import ViewOrder from "./ViewOrder";
import {RequestCreateSellOrder} from "../../../api/sell-orders/createSellOrder";
import FormErrorsOutput from "../../form/FormErrorsOutput";
import {CentsToDollars, DollarsToCents} from "../../../utils/formatPrice";
import { GetProgramFees, GetProvisionalTax } from '../../../api';
import { getReceipt } from "../../../api/sell-orders/getReceipt";
import usePrint from "../../../hooks/usePrint";
import { specialtyItemTotalsCents, isSpecialtyLineComplete } from "../../sell-specialty-items/specialtyItemTotals";


const CreateSellOrder = ({setShowCreateItem, onCreated}) => {

    const { t } = useTranslation();

    const Steps = Object.freeze({
        ADD_CARD: 1,
        ADD_PAYMENT: 2,
        FINALIZE: 3,
        VIEW_ORDER: 4,
        CONFIRMATION: 5
    });

    const [formData, setFormData] = useState({
        corporate_customer_id: "",
        client_company_name: "",
        subtotal_cost: 0,
        total_cost: 0,
        total_tax: 0,
        total_service_fee: 0,
        office_id: null,
        office_name: '',
        quantity: 0,
        cards: [],
        specialty_items: [],
        specialty_items_tax: 0,
        service_fee_tax: 0,
        payments: [],
        program_id: null,
        sell_order_small_batch_fee: null,
        enable_tax_on_activation_fee: false,
        province_code: '',
        provisional_tax_rate: 0,
        skip_activation_fee: false
    });

    const [sendingData, setSendingData] = useState(false);
    const [activeStep, setActiveStep] = useState(Steps.ADD_CARD);
    const [progress, setProgress] = useState(10);
    const [disabledContinueButton, setDisabledContinueButton] = useState(true);
    const [visibleContinueButton, setVisibleContinueButton] = useState(true);
    const [visibleCreateButton, setVisibleCreateButton] = useState(false);
    const [visibleDismissButton, setVisibleDismissButton] = useState(false);
    const [disabledBackButton, setDisabledBackButton] = useState(true);
    const [visibleBackButton, setVisibleBackButton] = useState(true);
    const [disabledCreateButton, setDisabledCreateButton] = useState(true);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState([]);
    const [createdOrder, setCreatedOrder] = useState({});
    const [prnError, setPrnError] = useState(null);

    const { handlePrint: handlePrintReceipt } = usePrint(getReceipt);

    const handleBackButtonClick = () => {
        setActiveStep(activeStep - 1);
    }

    const handleCreateButton = async (e) => {
        e.preventDefault();
        setSendingData(true);
        try {
            const {data} = await RequestCreateSellOrder({
                corporate_customer_id: formData.corporate_customer_id,
                total_cost: DollarsToCents(formData.total_cost), // Cents
                total_tax: DollarsToCents(formData.total_tax),
                total_service_fee: DollarsToCents(formData.total_service_fee),
                office_id: formData.office_id,
                quantity: formData.quantity,
                cards: formData.cards.map(card => ({
                    processor_reference: card.processor_reference,
                    load_amount: DollarsToCents(card.load_amount)
                })),
                payments: formData.payments.map(payment => ({
                    amount: DollarsToCents(payment.amount),
                    payment_type: payment.payment_type.value,
                    confirmation_code: payment.confirmation_code,
                })),
                // Only the item and the quantity: bps-go snapshots the name,
                // price and tax from the catalog itself.
                specialty_items: (formData.specialty_items ?? []).map(line => ({
                    specialty_item_id: line.specialty_item_id,
                    quantity: Number(line.quantity),
                })),
                skip_activation_fee: formData.skip_activation_fee
            });
            setSendingData(false);
            setActiveStep(Steps.CONFIRMATION);
            setCreatedOrder(data);
            // Refetch now so the list behind the popup is current by the time
            // it is dismissed.
            onCreated?.();
        } catch (error){
            setError(error.message);
            if (error.data) {
                setFieldErrors(error.data);
            }
            setSendingData(false);
            console.log('Failed to create sell order with error:', error);
        }
    }

    // Default actions on step change
    useEffect(() => {

        setError('');

        // Back button enabled by default on activeStep larger than 1
        if (activeStep > 1) {
            setDisabledBackButton(false);
        } else {
            setDisabledBackButton(true);
        }

        // Add Card
        if(activeStep === Steps.ADD_CARD){
            setProgress(10);
            setVisibleCreateButton(false);
            setVisibleContinueButton(true);
        }

        // Add Payment
        if(activeStep === Steps.ADD_PAYMENT){
            setProgress(50);
            setDisabledCreateButton(true);
            setVisibleContinueButton(false);
            setVisibleCreateButton(true);
        }

        //Finalize - skipped
        // if(activeStep === Steps.FINALIZE){
        //     setProgress(65);
        //     setVisibleContinueButton(false);
        //     setVisibleCreateButton(true);
        // } else {
        //     setVisibleContinueButton(true);
        //     setVisibleCreateButton(false);
        // }

        //View Order
        if (activeStep === Steps.VIEW_ORDER){
            setProgress(95);
            setVisibleContinueButton(false);
            setVisibleCreateButton(false);
        }

        // Confirmation Message
        if (activeStep === Steps.CONFIRMATION){
            setProgress(100);
            setVisibleCreateButton(false);
            setVisibleContinueButton(false);
            setVisibleBackButton(false);
            setVisibleDismissButton(true);
        }

    }, [activeStep]);


    useEffect(() => {
        if(activeStep === Steps.ADD_CARD){

            let allCardsValid = true;

            const specialtyItems = formData.specialty_items ?? [];

            // An order needs something on it, but either side may supply it.
            if(!formData.cards.length && !specialtyItems.length){
                allCardsValid = false;
            }

            // Every item row that exists must name an item and a quantity.
            specialtyItems.forEach((line) => {
                if (!isSpecialtyLineComplete(line)) {
                    allCardsValid = false;
                }
            });
            // validate duplicate processor references
            const processorReferences = formData.cards.map(card => card.processor_reference).filter(c => c);
            const hasDuplicateProcessors = processorReferences.some((ref, index) => processorReferences.indexOf(ref) !== index);
            if (hasDuplicateProcessors) {
                allCardsValid = false;
                setPrnError('Duplicate processor references found, please add unique processor references.');
            } else {
                setPrnError(null);
            }
            formData.cards.forEach((el) => {
                if (!el.processor_reference || !el.card_type || !(el.load_amount > 0)) {
                    allCardsValid = false;
                }
            });

            // A program is resolved from a card, so a specialty-items-only order
            // has none and must not wait for one.
            const programResolved = !formData.cards.length || formData.program_id;

            if(allCardsValid && formData.office_id && programResolved){
                setDisabledContinueButton(false);
            } else {
                setDisabledContinueButton(true);
            }
        }

        if(activeStep === Steps.ADD_PAYMENT){

            let allPaymentsValid = true;

            if(!formData.payments.length){
                allPaymentsValid = false;
            }
            formData.payments.forEach((el) => {
                if (!el.payment_type || !el.confirmation_code || !(el.amount > 0)) {
                    allPaymentsValid = false;
                }
            });

            if(allPaymentsValid && formData.office_id && (formData.total_cost - formData.total_collected) === 0){
                setDisabledCreateButton(false);
            } else {
                setDisabledCreateButton(true);
            }
        }
    }, [activeStep, formData]);

    useEffect(() => {
      async function getProgramFees() {
        // get program fees
        try {
          const programFeeRes = await GetProgramFees({
            program_id: formData.program_id,
          });
          // Functional update: this lands after an await, and spreading the
          // formData captured when the effect ran would revert anything typed
          // while the request was in flight.
          setFormData(prev => ({
            ...prev,
            sell_order_small_batch_fee: CentsToDollars(programFeeRes?.data?.sell_order_small_batch_fee ?? 0),
            enable_tax_on_activation_fee: programFeeRes?.data?.enable_tax_on_activation_fee ?? false,
          }));
        } catch (error) {
          console.log(error);
        }
      }
      if (formData.program_id) {
        getProgramFees();
      }
    }, [formData?.program_id]);

    useEffect(() => {
        async function getProvisioanlTax() {
          // get program fees
          try {
            const programFeeRes = await GetProvisionalTax({
              province_code: formData.province_code,
            });
            // Same reason as the fee lookup above: only this field may change.
            setFormData(prev => ({
              ...prev,
              provisional_tax_rate: (programFeeRes?.data?.tax_rate || 0) / 100,
            }));
          } catch (error) {
            console.log(error);
          }
        }
        if (formData.province_code) {
          getProvisioanlTax();
        }
      }, [formData?.province_code]);

    const MAXCARDLIMIT = 10;

    useEffect(() => {
        const FEE_PER_CARD = Number(formData?.sell_order_small_batch_fee ?? 0);
        const PROVISIONAL_TAX_RATE = Number(formData?.provisional_tax_rate ?? 0);
        const cards = formData?.cards ?? [];
        const cardsCount = cards.length;
        // Specialty items never count toward the small-batch threshold, and a
        // card-free order carries no activation fee at all.
        const serviceFeeApplicable = cardsCount > 0 && cardsCount < MAXCARDLIMIT;

        // Every figure below is computed in whole cents, the way bps-go does it.
        // Working in dollars let an unrounded fee tax (0.195) reach the total
        // while the chip displayed 0.20, so the parts stopped summing to the
        // whole and bps-go rejected the order on its total_cost check.
        const cardsSubtotalCents = cards.reduce(
            (acc, card) => acc + DollarsToCents(card.load_amount || 0), 0);

        const itemTotals = specialtyItemTotalsCents(formData?.specialty_items ?? [], PROVISIONAL_TAX_RATE);

        // skip_activation_fee zeroes the fee and its tax, never the item tax.
        const skipFee = Boolean(formData.skip_activation_fee);
        const serviceFeeCents = serviceFeeApplicable && !skipFee
            ? DollarsToCents(FEE_PER_CARD) * cardsCount
            : 0;
       
        const serviceFeeTaxCents = formData.enable_tax_on_activation_fee
            ? Math.round(serviceFeeCents * PROVISIONAL_TAX_RATE)
            : 0;

        const subtotalCents = cardsSubtotalCents + itemTotals.amount;
        const totalTaxCents = serviceFeeTaxCents + itemTotals.tax;
        const totalCostCents = subtotalCents + serviceFeeCents + totalTaxCents;

        const toDollars = (cents) => Number(CentsToDollars(cents));

        setFormData(prev => ({
            ...prev,
            subtotal_cost: toDollars(subtotalCents),
            total_cost: toDollars(totalCostCents),
            total_tax: toDollars(totalTaxCents),
            total_service_fee: toDollars(serviceFeeCents),
            // Kept apart for the payment step's two tax chips; the order itself
            // stores one total_tax.
            specialty_items_tax: toDollars(itemTotals.tax),
            service_fee_tax: toDollars(serviceFeeTaxCents),
        }));

        // eslint-disable-next-line
    }, [formData.cards, formData.specialty_items, formData.provisional_tax_rate, formData.sell_order_small_batch_fee, formData.skip_activation_fee, formData.enable_tax_on_activation_fee]);
    return (
        <Popup size="extra-large" withClose withOverlay onClose={() => setShowCreateItem(false)}>
            <div className="bulk-create-cards">
                <div className="multistep-form">
                    <div className="multistep-form__progress-bar">
                        <ProgressBar
                            title={t("Create Sell Order")}
                            steps={[
                                {
                                    active: (activeStep >= Steps.ADD_CARD),
                                    name: t("Add Card")
                                },
                                {
                                    active: (activeStep >= Steps.ADD_PAYMENT),
                                    name: t("Add Payment")
                                },
                                // {
                                //     active: (activeStep >= Steps.FINALIZE),
                                //     name: t("Finalize / Submit")
                                // },
                                {
                                    active: (activeStep >= Steps.VIEW_ORDER),
                                    name: t("View Order")
                                }
                            ]}
                            progress={progress}
                            class="mb-4"
                        />
                    </div>
                    <div className="multistep-form__content mb-2">
                        {activeStep === Steps.ADD_CARD &&
                            <AddCard formData={formData} setFormData={setFormData} prnError={prnError} />}
                        {activeStep === Steps.ADD_PAYMENT &&
                            <AddPayment formData={formData} setFormData={setFormData}/>}
                        {activeStep === Steps.FINALIZE &&
                            <Finalize formData={formData} setFormData={setFormData}/>}
                        {activeStep === Steps.VIEW_ORDER &&
                            <ViewOrder/>}
                        {activeStep === Steps.CONFIRMATION &&
                            <OrderCreationMessage formData={formData} setFormData={setFormData}/>}
                    </div>
                    <FormErrorsOutput error={error} fieldErrors={fieldErrors}/>
                    <div className="multistep-form__navigation">
                        {visibleBackButton && <button
                            className="btn btn-secondary btn-medium"
                            onClick={handleBackButtonClick}
                            disabled={disabledBackButton}
                        >
                            <ArrowLeft/>{t("Back")}
                        </button>}
                        {visibleContinueButton && <button
                            className="btn btn-primary btn-medium"
                            onClick={e => setActiveStep(activeStep + 1)}
                            disabled={disabledContinueButton}
                        >
                            {t("Continue")}
                        </button>}

                        {visibleCreateButton && <button
                            className={`btn btn-primary btn-medium ${sendingData ? 'btn-loading' : ''}`}
                            onClick={handleCreateButton}
                            disabled={disabledCreateButton || sendingData}
                        >
                            {sendingData ? <LoadingIcon/> : ""}
                            Place Order
                        </button>}

                        {visibleDismissButton  && <div className="text-right" style={{width: '100%'}}>
                            <button
                                className={`btn btn-secondary btn-medium mr-2`}
                                onClick={() => handlePrintReceipt({
                                    requestParams: { id: createdOrder.id },
                                    name: `order-${createdOrder.id}`,
                                    width: "",
                                    height: "",
                                    download: false,
                                })}
                            >
                                <PrintIcon/> {t("Print Receipt")}
                            </button>
                            <button
                                className={`btn btn-primary btn-medium`}
                                onClick={e => setShowCreateItem(false)}
                            >{t("Done")}
                            </button>
                        </div>}
                    </div>
                </div>

            </div>
        </Popup>
    );
};

export default CreateSellOrder;
